import { useNavigate } from '@tanstack/react-router';
import { LoginForm, AuthLayout } from '@/features/auth';
import { useLogin, useAuth } from '@/features/auth';
import { useErrorHandler } from '@/hooks';
import { useEffect, useState } from 'react';

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { mutate: login, isPending, error } = useLogin();
  const { handleError } = useErrorHandler('Login');
  // Interactive override: cleared on submit, set by the onError callback.
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Derive the displayed error during render rather than mirroring the mutation
  // error into state via an effect. The override takes precedence so a fresh
  // submit can clear a stale message.
  const loginError =
    submitError ?? (error instanceof Error ? error.message : error ? 'An error occurred during login' : null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, navigate]);

  // Surface the genuine side-effect (toast) when a mutation error appears.
  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error, handleError]);

  const handleLogin = async (credentials: { email: string; password: string }) => {
    setSubmitError(null);
    login(credentials, {
      onSuccess: () => {
        // Navigate to home page on successful login
        navigate({ to: '/' });
      },
      onError: (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Invalid email or password';
        setSubmitError(errorMessage);
      },
    });
  };

  return (
    <AuthLayout
      title="Sign in to your account"
      subtitle="Enter your credentials to continue"
      footer={
        <>
          Forgot your password?{' '}
          <a
            href="#"
            className="
              font-medium text-action-primary
              hover:underline
            "
          >
            Reset it
          </a>
        </>
      }
    >
      <LoginForm onSubmit={handleLogin} isLoading={isPending} error={loginError} />
    </AuthLayout>
  );
}
